// packages/core/src/learning/schools/efficient-market.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'john-bogle'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const EFFICIENT_MARKET_EXHIBIT: SchoolExhibitEntry = {
	id: 'efficient-market',
	tagline:
		'Efficient market believes the price usually knows more than you do.',
	worldviewSummary:
		'Efficient market is paying attention to how fast information gets absorbed into prices. This lens protects you from assuming your opinion is automatically special just because it feels convincing. It accepts a humble tradeoff: if markets are hard to outguess, then costs, patience, and behavior matter more than constant prediction. The blind spot is that this worldview can make markets sound cleaner than real life, where bubbles, fear, incentives, and human behavior still matter. At its best, efficient market thinking helps users stop treating investing like a contest of cleverness.',
	coreBeliefs: [
		'Efficient market believes prices already reflect a lot of public information.',
		'Efficient market believes most people overestimate their ability to outguess millions of other buyers and sellers.',
		'Efficient market believes costs matter because every fee makes the hurdle higher.',
		'Efficient market believes humility is a useful investing skill.',
		'Efficient market believes constant prediction can become expensive entertainment.',
		'Efficient market believes behavior often matters more than having a hot opinion.',
	],
	warnsAgainst: [
		'Assuming a popular story is still an undiscovered opportunity.',
		'Mistaking confidence for an edge.',
		'Trading too often because the market feels readable in the moment.',
		'Ignoring costs, taxes, timing mistakes, and emotional decisions.',
	],
	helps: 'This lens helps people who want a calmer way to think about markets without pretending they can always spot what others missed. It may not fit users who genuinely study businesses, accept concentration risk, or want to explore active strategies with full awareness of the tradeoffs.',
	figures: [],
	canon: [
		{
			title: 'Efficient Capital Markets: A Review of Theory and Empirical Work',
			author: 'Eugene Fama',
			oneLineIdea:
				'Market prices often move as if public information is already being fought over by many people at once.',
		},
		{
			title: 'A Random Walk Down Wall Street',
			author: 'Burton Malkiel',
			oneLineIdea:
				'Trying to predict short term market moves is harder than it feels, especially after costs and behavior enter the picture.',
		},
		{
			title: 'The Arithmetic of Active Management',
			author: 'William Sharpe',
			oneLineIdea:
				'Before costs, active investors as a group own the market. After costs, the average active dollar has a tougher job.',
		},
		{
			title: 'Index fund investing',
			oneLineIdea:
				'Owning broad markets is one practical response to the idea that outguessing prices is difficult.',
		},
		{
			title: 'Price as information',
			oneLineIdea:
				'A market price is not just a number. It is a crowd of expectations, fears, incentives, and available information colliding.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-efficient-market',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare your market opinion against your real plan',
			action: {
				type: 'openSimulator',
				label: 'Test a calmer plan',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check whether your current numbers need prediction or patience',
			action: {
				type: 'openDashboard',
				label: 'Review your money picture',
			},
		},
		{
			label: 'See what steady saving does before chasing clever moves',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Write the edge you think you have',
			action: {
				type: 'noToolAction',
				label: 'Name your edge',
				prompt: 'Write what you believe you know that the market may not already know, then write what evidence would prove you wrong.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Efficient market thinking starts with a brutal question: what do you know that the price does not?',
			kind: 'hook',
		},
		{
			line: 'When did a market opinion feel obvious to you, then turn out to be crowded already?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users test whether their plan needs prediction or just better behavior.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show someone a hot market take, then ask what information is already inside the price.',
			kind: 'creator-prompt',
		},
		{
			line: 'The hidden cost of clever investing is not only money. It is attention.',
			kind: 'hook',
		},
		{
			line: 'Ask a creator where efficient market thinking protects beginners, and where it gets too tidy.',
			kind: 'creator-prompt',
		},
		{
			line: 'What would change if you stopped treating every market story like a personal opportunity?',
			kind: 'interview-question',
		},
		{
			line: 'Markets are not magic. But they are harder to outsmart than your confidence admits.',
			kind: 'hook',
		},
	],
	revision: 1,
};
